import {
   ACCIDENT_PARTIES_AT_FAULT_OPTIONS,
   CONVICTION_CODES,
   DRIVING_LICENSE_DURATIONS_NEEDING_LICENSE_DATE_LIST,
   DRIVING_LICENSE_TYPE_OPTIONS,
   DVLA_MEDICAL_CONDITION_OPTIONS,
   OTHER_VEHICLE_USAGE_OPTIONS,
   YES_NO_CHOICE_OPTIONS
} from "@constants/dropdown-choices";
import {isDateGreaterThenToday} from "@utils/date-time";
import {isInLabelValueArrayAsString, isValidDateTimeString, isValidPositiveNumber} from "@utils/validators";
import {isNotStringOrEmptyString} from "@utils/string";

/**
 * @typedef {object} data
 * @property {string} addAdditionalComments
 * @property {string} additionalDrivers
 * @property {string} additionalDrivingQualificationDate
 * @property {string} additionalDrivingQualifications
 * @property {string} britishDrivingLicenseNumber
 * @property {string} vanAlarmsOrImmobiliser
 * @property {string} vanDoorNumber
 * @property {string} vanEngineSize
 * @property {string} vanEstimatedValue
 * @property {string} vanImpoundedWithPolice
 * @property {string} vanMakeAndModel
 * @property {string} vanParkingAtDay
 * @property {string} vanParkingAtNight
 * @property {string} vanPurchased
 * @property {string} vanPurchaseDate
 * @property {string} vanRegistrationNumber
 * @property {string} vanRegistrationYear
 * @property {string} vanSeatNumber
 * @property {string} vanTransmissionType
 * @property {string} contactEmail
 * @property {string} contactPhone
 * @property {string} currentOrPreviousInsurerName
 * @property {string} discoveryType
 * @property {string} drivingLicenseDate
 * @property {string} drivingLicenseDuration
 * @property {string} drivingLicenseType
 * @property {string} dvlaMedicalConditions
 * @property {string} estimatedAnnualMileage
 * @property {string} hasAccidentClaimsInLast5Years
 * @property {string} hasChildrenUnder16
 * @property {string} hasModifications
 * @property {string} hasMotoringConvictionsInLast5Years
 * @property {string} hasNonMotoringCriminalConvictions
 * @property {string} insuranceCoverageStartDate
 * @property {string} insuranceCoverageType
 * @property {string} insurancePaymentType
 * @property {string} insurerName
 * @property {string} isBornInUk
 * @property {string} isVanKeptOnSameAddress
 * @property {string} isVanRegistrationNumKnown
 * @property {string} isHomeOwner
 * @property {string} legalOwner
 * @property {string} mainOccupation
 * @property {string} manufacturerModel
 * @property {string} manufacturerName
 * @property {string} modificationDetails
 * @property {string} modificationType
 * @property {string} motorAccidentsList
 * @property {string} noClaimsDiscount
 * @property {string} occupationIndustry
 * @property {string} otherVehicleUsage
 * @property {string} provideDrivingLicenseNumber
 * @property {string} registeredKeeper
 * @property {string} renewalPriceOfPreviousInsurer
 * @property {string} shouldAddAdditionalDrivers
 * @property {string} termsAgreement
 * @property {string} vehicleKeeperAddress
 * @property {string} vehicleUsageType
 * @property {string} voluntaryExcessAmounts
 * @property {string} wasInsuranceDeclinedBefore
 */

export const ValidateStep3Data = (data) => {

    const msgList = [/*{
        name : 'test',
        message : 'Hold!',
    }*/];
    
    if(!isInLabelValueArrayAsString(data.drivingLicenseType,DRIVING_LICENSE_TYPE_OPTIONS)) {
       
       msgList.push({
          name: 'drivingLicenseType',
          message: 'Please select driving license type'
       });
       
    }
    
    if(isNotStringOrEmptyString(data.drivingLicenseDuration)) {
       
       msgList.push({
          name : 'drivingLicenseDuration',
          message: 'Please select driving license duration'
       });
       
    }
    
    if(DRIVING_LICENSE_DURATIONS_NEEDING_LICENSE_DATE_LIST.includes(data.drivingLicenseDuration)) {
       
       if(!isValidDateTimeString(data.drivingLicenseDate)) {
          
          msgList.push({
             name: `drivingLicenseDate`,
             message: `Please enter driving license obtaining date`
          });
          
       } else if(isDateGreaterThenToday(data.drivingLicenseDate)) {
          
          msgList.push({
             name: `drivingLicenseDate`,
             message: `Driving license date cannot be in the future`
          });
       
       }
    
    }
    
    if(!isInLabelValueArrayAsString(data.dvlaMedicalConditions,DVLA_MEDICAL_CONDITION_OPTIONS)) {
       msgList.push({
          name: 'dvlaMedicalConditions',
          message: 'Please select DVLA medical conditions'
       });
    }
    
    if(!isInLabelValueArrayAsString(data.isBornInUk,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
          name: 'isBornInUk',
          message: 'Please select if you are born in UK'
       });
    }
    
    if('No' === data.isBornInUk) {
    
       if(!isValidDateTimeString(data.lastDateBecameUKResident)) {
          msgList.push({
             name: `lastDateBecameUKResident`,
             message: `Please enter last date you became UK resident`
          });
       } else if(isDateGreaterThenToday(data.lastDateBecameUKResident)) {
          msgList.push({
             name: `lastDateBecameUKResident`,
             message: `Last date you became UK resident cannot be in the future`
          });
       }
    
    }
    
    if(!isInLabelValueArrayAsString(data.otherVehicleUsage,OTHER_VEHICLE_USAGE_OPTIONS)) {
       
       msgList.push({
          name: 'otherVehicleUsage',
          message: 'Please specify if there are usage of other vehicles'
       });
       
    }
    
    if(!isInLabelValueArrayAsString(data.hasNonMotoringCriminalConvictions,YES_NO_CHOICE_OPTIONS)) {
       
       msgList.push({
          name: `hasNonMotoringCriminalConvictions`,
          message: `Please select if you have unspent NON-Motoring Criminal Convictions`
       });
       
    }
    
    if(!(data.additionalDrivingQualifications === '' || data.additionalDrivingQualifications === 'No')) {
       
       if(!isValidDateTimeString(data.additionalDrivingQualificationDate)) {
          msgList.push({
             name: `additionalDrivingQualificationDate`,
             message: `Please enter date of additional driving qualification obtaining`
          });
       } else if(isDateGreaterThenToday(data.additionalDrivingQualificationDate)) {
          msgList.push({
             name: `additionalDrivingQualificationDate`,
             message: `Date of additional driving qualification obtaining cannot be in the future`
          });
       }
       
    }
    
    if(!isInLabelValueArrayAsString(data.hasAccidentClaimsInLast5Years,YES_NO_CHOICE_OPTIONS)) {
    
       
       msgList.push({
          name: `hasAccidentClaimsInLast5Years`,
          message: `Please select if you have motor accidents or claims in last 5 years`
       });
       
    }
    
    if(data.hasAccidentClaimsInLast5Years === 'Yes') {
       
       validateMotorAccidentsList(data,msgList);
       
    }
    
    if(!isInLabelValueArrayAsString(data.hasMotoringConvictionsInLast5Years,YES_NO_CHOICE_OPTIONS)) {
       
       msgList.push({
          name: `hasMotoringConvictionsInLast5Years`,
          message: `Please select if you had any motoring convictions in last 5 years`
       });
       
    }
    
    if(data.hasMotoringConvictionsInLast5Years === 'Yes') {
       
       validateMotorConvictionsList(data,msgList);
    
    }
    
    

    return msgList;

}

const validateMotorConvictionsList = (data,msgList) => {
   
   let motorConvictionsList = data.motorConvictionsList && Array.isArray(data.motorConvictionsList) ? data.motorConvictionsList : [];
 
   if(motorConvictionsList.length === 0) {
      msgList.push({
         name: `motorConvictionsList`,
         message: `Please add one or more motor convictions record`
      });
      
      return;
   }
   
   motorConvictionsList.forEach((conviction,convictionIndex) => {
      
       
       if(!isValidDateTimeString(conviction.date)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'date'),
             message: `Please select motor conviction date for conviction record #${convictionIndex + 1}`
          })
       } else if(isDateGreaterThenToday(conviction.date)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'date'),
             message: `Motor conviction date cannot be in the future for conviction record #${convictionIndex + 1}`
          })
       }
       
       if(!isInLabelValueArrayAsString(conviction.code,CONVICTION_CODES)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'code'),
             message: `Please select motor conviction code for conviction record #${convictionIndex + 1}`
          })
       }
       
       if(isNotStringOrEmptyString(conviction.points)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'points'),
             message: `Please enter motor conviction points for conviction record #${convictionIndex + 1}`
          })
       } else if(!isValidPositiveNumber(conviction.points)) {
          
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'points'),
             message: `Please enter valid motor conviction points for conviction record #${convictionIndex + 1}`
          })
          
       }
       
       if(isNotStringOrEmptyString(conviction.fines)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'fines'),
             message: `Please enter motor conviction fines for conviction record #${convictionIndex + 1}`
          })
       } else if(!isValidPositiveNumber(conviction.fines,true)) {
          
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'fines'),
             message: `Please enter valid motor conviction fines for conviction record #${convictionIndex + 1}`
          })
       }
      
       if(isNotStringOrEmptyString(conviction.banLengthInMonths)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'banLengthInMonths'),
             message: `Please enter motor conviction ban length in months for conviction record #${convictionIndex + 1}`
          })
       } else if(!isValidPositiveNumber(conviction.banLengthInMonths,true)) {
          
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'banLengthInMonths'),
             message: `Please enter valid motor conviction ban length in months for conviction record #${convictionIndex + 1}`
          })
       }
       
       if(!isInLabelValueArrayAsString(conviction.wasDriverBreathalysed,YES_NO_CHOICE_OPTIONS)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'wasDriverBreathalysed'),
             message: `Please select if driver was breathalysed for conviction record #${convictionIndex + 1}`
          })
       }
       
       if(!isInLabelValueArrayAsString(conviction.wasOffenceAccidentRelated,YES_NO_CHOICE_OPTIONS)) {
          msgList.push({
             name: generateNameForMotorConvictions(convictionIndex,'wasOffenceAccidentRelated'),
             message: `Please select if offence was accident related for conviction record #${convictionIndex + 1}`
          })
       }
   
   });
   
}

const validateMotorAccidentsList = (data,msgList) => {
   
   let motorAccidentsList = data.motorAccidentsList && Array.isArray(data.motorAccidentsList) ? data.motorAccidentsList : [];
   
   if(motorAccidentsList.length === 0) {
      
      msgList.push({
         name: `motorAccidentsList`,
         message: `Please add one or more incidents record`
      });
      
      return;
      
   }
   
   motorAccidentsList.forEach((accident,accidentIndex) => {
      
      if(isNotStringOrEmptyString(accident.type)) {
         msgList.push({
            name: generateNameForMotorAccidents(accidentIndex,'type'),
            message: `Please select incident type for incident record #${accidentIndex + 1}`
         });
      }
      
      if(!isValidDateTimeString(accident.date)) {
         msgList.push({
            name: generateNameForMotorAccidents(accidentIndex,'date'),
            message: `Please select incident date for incident record #${accidentIndex + 1}`
         });
      } else if(isDateGreaterThenToday(accident.date)) {
         msgList.push({
            name: generateNameForMotorAccidents(accidentIndex,'date'),
            message: `Incident date cannot be in the future for incident record #${accidentIndex + 1}`
         });
      }
      
     if(!isInLabelValueArrayAsString(accident.partyAtFault,ACCIDENT_PARTIES_AT_FAULT_OPTIONS)) {
        msgList.push({
           name: generateNameForMotorAccidents(accidentIndex,'partyAtFault'),
           message: `Please select party at fault for incident record #${accidentIndex + 1}`
        });
     }
      
   })
   
}

const generateNameForMotorAccidents = (accidentIndex,name) => {
   return `motorAccidentsList.${accidentIndex}.${name}`;
}

const generateNameForMotorConvictions = (convictionIndex,name) => {
   return `motorConvictionsList.${convictionIndex}.${name}`;
}